import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  InputAdornment,
  Alert,
  Typography,
  IconButton,
} from "@mui/material";
import type { Product } from "../database/db";
import { useSettings } from "../contexts/SettingsContext";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import ShoppingBasketIcon from "@mui/icons-material/ShoppingBasket";
import ImageIcon from "@mui/icons-material/Image";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDropzone } from "react-dropzone";
import imageCompression from "browser-image-compression";
import { formatFileSize } from "../utils/formatters";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";

interface ProductFormProps {
  open: boolean;
  onClose: () => void;
  onSave: (product: Omit<Product, "id">) => void;
  initialProduct?: Product;
}

export default function ProductForm({
  open,
  onClose,
  onSave,
  initialProduct,
}: ProductFormProps) {
  const { settings } = useSettings();
  const [name, setName] = useState("");
  const [sku, setSku] = useState("");
  const [price, setPrice] = useState("");
  const [description, setDescription] = useState("");
  const [originalStockQuantity, setOriginalStockQuantity] = useState("");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [imageBlob, setImageBlob] = useState<Blob | null>(null);
  const [imageMimeType, setImageMimeType] = useState<string>("");
  const [imageDetails, setImageDetails] = useState<{
    width: number;
    height: number;
    size: number;
  } | null>(null);
  const [skuManuallyEdited, setSkuManuallyEdited] = useState(false);
  const [isEditingSku, setIsEditingSku] = useState(false);

  useEffect(() => {
    if (initialProduct) {
      setName(initialProduct.name);
      setSku(initialProduct.sku);
      setPrice(initialProduct.price.toString());
      setDescription(initialProduct.description);
      setOriginalStockQuantity(initialProduct.originalStockQuantity.toString());
      setImageBlob(initialProduct.imageBlob || null);
      setImageMimeType(initialProduct.imageMimeType || "");
      setSkuManuallyEdited(true); // Add this line to prevent SKU updates when editing
    } else {
      resetForm();
    }
  }, [initialProduct]);

  useEffect(() => {
    if (!skuManuallyEdited) {
      setSku(generateSKU(name));
    }
  }, [name, skuManuallyEdited]);

  const generateSKU = (productName: string): string => {
    return productName
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "")
      .slice(0, 8)
      .padEnd(8, "0");
  };

  const resetForm = () => {
    setName("");
    setSku("");
    setPrice("");
    setDescription("");
    setOriginalStockQuantity("");
    setErrors({});
    setImageBlob(null);
    setImageMimeType("");
    setImageDetails(null);
    setSkuManuallyEdited(false);
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === "") {
      setPrice("");
      return;
    }

    const number = parseFloat(value);
    if (!isNaN(number) && number !== 0) {
      // Round to the correct number of decimal places
      const rounded = Number(number.toFixed(settings.currencyRounding));
      setPrice(rounded.toString());
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!name.trim()) newErrors.name = "Name is required";
    if (!price.trim()) newErrors.price = "Price is required";
    else if (isNaN(Number(price)) || Number(price) === 0)
      newErrors.price = "Price cannot be zero";
    if (originalStockQuantity.trim())
      if (
        isNaN(Number(originalStockQuantity)) ||
        !Number.isInteger(Number(originalStockQuantity)) ||
        Number(originalStockQuantity) < 0
      )
        newErrors.originalStockQuantity =
          "Stock quantity must be a positive whole number";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSave({
        name,
        sku,
        price: Number(price),
        description,
        originalStockQuantity: Number(originalStockQuantity),
        imageBlob: imageBlob || undefined,
        imageMimeType: imageMimeType || undefined,
      });
      resetForm();
      onClose();
    }
  };

  const isDiscount = Number(price) < 0;

  const getImageDimensions = (
    blob: Blob
  ): Promise<{ width: number; height: number }> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
        URL.revokeObjectURL(img.src);
      };
      img.src = URL.createObjectURL(blob);
    });
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];

      try {
        // Compress the image
        const compressedFile = await imageCompression(file, {
          maxSizeMB: 0.3,
          maxWidthOrHeight: 800,
          useWebWorker: true,
        });

        // Get dimensions of compressed image
        const dimensions = await getImageDimensions(compressedFile);

        setImageBlob(compressedFile);
        setImageMimeType(file.type);
        setImageDetails({
          width: dimensions.width,
          height: dimensions.height,
          size: compressedFile.size,
        });
      } catch (error) {
        console.error("Error compressing image:", error);
      }
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/jpeg": [".jpg", ".jpeg"],
      "image/png": [".png"],
    },
    maxFiles: 1,
  });

  const getImagePreview = () => {
    if (imageBlob) {
      return URL.createObjectURL(imageBlob);
    }
    return null;
  };

  const handleRemoveImage = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent dropzone from triggering
    setImageBlob(null);
    setImageMimeType("");
    setImageDetails(null);
  };

  const handleSkuChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Convert to uppercase and remove invalid characters
    const formattedSku = value.toUpperCase().replace(/[^A-Z0-9-]/g, "");
    setSku(formattedSku);
    setSkuManuallyEdited(true);
  };

  const handleResetSku = () => {
    setSkuManuallyEdited(false);
    setSku(generateSKU(name));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {isDiscount ? (
          <LocalOfferIcon color="secondary" />
        ) : (
          <ShoppingBasketIcon color="primary" />
        )}
        {initialProduct ? "Edit" : "Add"}{" "}
        {isDiscount ? "Discount Coupon" : "Product"}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
            required
          />
          <TextField
            label="SKU"
            value={sku}
            onChange={handleSkuChange}
            fullWidth
            disabled={initialProduct && !isEditingSku}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {initialProduct && (
                    <IconButton
                      onClick={() => setIsEditingSku(!isEditingSku)}
                      edge="end"
                      size="small"
                      title={isEditingSku ? "Cancel SKU edit" : "Edit SKU"}
                      color={isEditingSku ? "error" : "default"}>
                      {isEditingSku ? <CloseIcon /> : <EditIcon />}
                    </IconButton>
                  )}
                  {(!initialProduct || isEditingSku) && skuManuallyEdited && (
                    <IconButton
                      onClick={handleResetSku}
                      edge="end"
                      size="small"
                      title="Reset SKU">
                      <RefreshIcon />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
          />
          {initialProduct && isEditingSku && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Warning: Changing the SKU will break the connection with existing
              sales history. Only change this if absolutely necessary.
            </Alert>
          )}
          <Alert severity="info">
            Negative prices create discount coupons that can be applied during
            checkout
          </Alert>
          <TextField
            label={isDiscount ? "Discount Amount" : "Price"}
            value={price}
            onChange={handlePriceChange}
            error={!!errors.price}
            helperText={errors.price}
            fullWidth
            required
            type="number"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {settings.currencySymbol}
                </InputAdornment>
              ),
              inputProps: {
                step: Math.pow(10, -settings.currencyRounding),
              },
            }}
          />
          <TextField
            label="Initial Stock Quantity"
            value={originalStockQuantity}
            onChange={(e) => setOriginalStockQuantity(e.target.value)}
            error={!!errors.originalStockQuantity}
            helperText={errors.originalStockQuantity}
            fullWidth
            type="number"
            inputProps={{ min: 0, step: 1 }}
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={3}
          />
          <Box
            {...getRootProps()}
            sx={{
              border: "2px dashed",
              borderColor: isDragActive ? "primary.main" : "grey.300",
              borderRadius: 1,
              p: 2,
              cursor: "pointer",
              textAlign: "center",
              "&:hover": {
                borderColor: "primary.main",
                bgcolor: "action.hover",
              },
              position: "relative", // For positioning the remove button
            }}>
            <input {...getInputProps()} />

            {imageBlob && (
              <IconButton
                onClick={handleRemoveImage}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  bgcolor: "background.paper",
                  "&:hover": {
                    bgcolor: "error.light",
                    color: "white",
                  },
                }}
                size="small">
                <DeleteIcon />
              </IconButton>
            )}

            <Box sx={{ mb: 2 }}>
              {imageBlob ? (
                <>
                  <img
                    src={getImagePreview() ?? ""}
                    alt="Product preview"
                    style={{
                      maxWidth: "200px",
                      maxHeight: "200px",
                      objectFit: "contain",
                    }}
                  />
                  {imageDetails && (
                    <Typography
                      variant="caption"
                      display="block"
                      color="text.secondary"
                      sx={{ mt: 1 }}>
                      {imageDetails.width} × {imageDetails.height}px •{" "}
                      {formatFileSize(imageDetails.size)}
                    </Typography>
                  )}
                </>
              ) : (
                <ImageIcon sx={{ fontSize: 60, color: "text.secondary" }} />
              )}
            </Box>

            <Typography color="text.secondary">
              {isDragActive
                ? "Drop the image here"
                : "Drag and drop a product image here, or click to select"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Accepts JPG and PNG. Images will be resized to max 800px and
              compressed to under 300KB.
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
